/**
 * Subscription Page - עמוד מנוי
 * Shows the user's current plan and lets them choose a paid plan via CardCom.
 *
 * Two products:
 *  - annual  : one-time 250₪ → Pro for a year.
 *  - monthly : 75₪/month הוראת קבע (CardCom-managed) — pending CardCom recurring
 *              doc, so the monthly purchase button is disabled ("בקרוב").
 *
 * Onboarding mode (?onboarding=true): shown right after the last document.
 * The user may pick a plan OR "continue free"; either records the decision so
 * they are not re-prompted. Chat stays locked (paywall) until they subscribe.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle2, CreditCard, Sparkles, CalendarClock, Tag, ShieldCheck, Apple } from 'lucide-react'
import api, { handleApiError } from '../services/api'
import { useAuthStore } from '../store/authStore'
import PaymentReassuranceModal from '../components/payment/PaymentReassuranceModal'
import type { PlanId, SubscriptionStatusResponse, CouponPreviewResponse } from '../types'

// Monthly הוראת קבע (CardCom Direct Debit / RecurringPayment.aspx) is wired up.
const MONTHLY_ENABLED = true

function formatHebrewDate(iso?: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

export default function SubscriptionPage() {
  const [status, setStatus] = useState<SubscriptionStatusResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [working, setWorking] = useState<PlanId | 'monthly-bit' | 'cancel' | 'defer' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [consent, setConsent] = useState(false)
  // Pending payment intent: opens the CardCom reassurance modal; the actual
  // checkout call + redirect happen only after the user confirms in the modal.
  const [pendingPay, setPendingPay] = useState<{ planId: PlanId; method: 'card' | 'bit' } | null>(null)

  // Coupon: a single code, validated against each plan (a code may apply to one
  // plan or both). previews[planId].valid drives the discounted price shown.
  const [couponInput, setCouponInput] = useState('')
  const [appliedCode, setAppliedCode] = useState('')
  const [couponPreviews, setCouponPreviews] = useState<Partial<Record<PlanId, CouponPreviewResponse>>>({})
  const [couponChecking, setCouponChecking] = useState(false)
  const [couponMessage, setCouponMessage] = useState<{ ok: boolean; text: string } | null>(null)

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { updateUser } = useAuthStore()
  const onboarding = searchParams.get('onboarding') === 'true'

  const loadStatus = () =>
    api
      .get<SubscriptionStatusResponse>('/payment/subscription')
      .then((res) => setStatus(res.data))
      .catch((err) => setError(handleApiError(err)))

  useEffect(() => {
    let cancelled = false
    api
      .get<SubscriptionStatusResponse>('/payment/subscription')
      .then((res) => {
        if (!cancelled) setStatus(res.data)
      })
      .catch((err) => {
        if (!cancelled) setError(handleApiError(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleApplyCoupon = async () => {
    const code = couponInput.trim()
    if (!code) return
    setCouponChecking(true)
    setCouponMessage(null)
    try {
      const [annualRes, monthlyRes] = await Promise.all([
        api.post<CouponPreviewResponse>('/payment/validate-coupon', { code, planId: 'annual' }),
        api.post<CouponPreviewResponse>('/payment/validate-coupon', { code, planId: 'monthly' }),
      ])
      const previews: Partial<Record<PlanId, CouponPreviewResponse>> = {
        annual: annualRes.data,
        monthly: monthlyRes.data,
      }
      setCouponPreviews(previews)
      setAppliedCode(code.toUpperCase())
      const anyValid = annualRes.data.valid || monthlyRes.data.valid
      if (anyValid) {
        const plans = [
          annualRes.data.valid ? 'שנתי' : null,
          monthlyRes.data.valid ? 'חודשי' : null,
        ].filter(Boolean).join(' ו')
        setCouponMessage({ ok: true, text: `הקופון הוחל על מסלול ${plans}.` })
      } else {
        setCouponMessage({ ok: false, text: annualRes.data.reason || monthlyRes.data.reason || 'קוד הקופון אינו תקף.' })
      }
    } catch (err) {
      setCouponMessage({ ok: false, text: handleApiError(err) })
    } finally {
      setCouponChecking(false)
    }
  }

  const handleClearCoupon = () => {
    setCouponInput('')
    setAppliedCode('')
    setCouponPreviews({})
    setCouponMessage(null)
  }

  const handleUpgrade = async (planId: PlanId, method: 'card' | 'bit' = 'card') => {
    setWorking(planId === 'monthly' && method === 'bit' ? 'monthly-bit' : planId)
    setError(null)
    setInfo(null)
    try {
      const couponCode = couponPreviews[planId]?.valid ? appliedCode : undefined
      const body: { planId: PlanId; method?: 'bit'; consent?: boolean; couponCode?: string } = { planId }
      if (method === 'bit') {
        // Manual one-time monthly (Bit) — no recurring consent needed.
        body.method = 'bit'
      } else if (planId === 'monthly') {
        // Card monthly is an auto-recurring standing order → consent required.
        body.consent = true
      }
      if (couponCode) body.couponCode = couponCode
      const res = await api.post<{ url: string }>('/payment/checkout', body)
      window.location.href = res.data.url
    } catch (err) {
      setError(handleApiError(err))
      setWorking(null)
      setPendingPay(null)
    }
  }

  const handleDeferFree = async () => {
    setWorking('defer')
    setError(null)
    try {
      await api.post('/payment/defer-plan')
      updateUser({ subscriptionDecisionAt: new Date().toISOString() })
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setError(handleApiError(err))
      setWorking(null)
    }
  }

  const handleCancel = async () => {
    if (!window.confirm('לבטל את המנוי החודשי? הגישה תישמר עד תום התקופה.')) return
    setWorking('cancel')
    setError(null)
    setInfo(null)
    try {
      const res = await api.post<{ message?: string }>('/payment/cancel')
      setInfo(res.data?.message || 'המנוי יבוטל בתום התקופה.')
      await loadStatus()
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setWorking(null)
    }
  }

  // Coupon-aware price for a plan: discounted value when a valid coupon applies,
  // otherwise the catalog base. Falls back to 0 if the catalog hasn't loaded.
  const effectiveAmount = (planId: PlanId): number => {
    const p = couponPreviews[planId]
    if (p?.valid && p.discountedIls != null) return p.discountedIls
    return plans?.[planId]?.amountIls ?? 0
  }

  const renderPrice = (planId: PlanId, base: number, suffix: string) => {
    const p = couponPreviews[planId]
    if (p?.valid && p.discountedIls != null) {
      return (
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-3xl font-bold text-gray-900">{p.discountedIls}</span>
          <span className="text-lg text-gray-600">{suffix}</span>
          <span className="text-lg text-gray-400 line-through">{base} ₪</span>
        </div>
      )
    }
    return (
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold text-gray-900">{base}</span>
        <span className="text-lg text-gray-600">{suffix}</span>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner w-12 h-12" />
      </div>
    )
  }

  const isPro = status?.plan === 'pro'
  const plans = status?.plans
  const sub = status?.subscription
  const paymentAvailable = status?.paymentAvailable ?? false
  // Manual monthly = paid via Bit (or any non-recurring monthly): no standing
  // order, so the user re-pays each month and access lapses on currentPeriodEnd.
  const isManualMonthly = sub?.planType === 'monthly' && sub?.recurring === false

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-gray-900 mb-2">המנוי שלי</h1>
        <p className="text-gray-600">נהל את המסלול שלך באפליקציה</p>
      </div>

      {onboarding && !isPro && (
        <div className="card mb-6 border-primary-200 bg-primary-50/40">
          <p className="text-gray-800 font-medium">
            כל המסמכים נחתמו — נותר רק לבחור מסלול כדי להתחיל לשוחח עם היועץ.
          </p>
          <p className="text-sm text-gray-600 mt-1">
            ניתן גם להמשיך בינתיים במסלול חינמי; הצ'אט ייפתח לאחר רכישת מסלול.
          </p>
        </div>
      )}

      {error && (
        <div className="card mb-6 border-red-200 bg-red-50" role="alert">
          <p className="text-red-700">{error}</p>
        </div>
      )}
      {info && (
        <div className="card mb-6 border-green-200 bg-green-50" role="status">
          <p className="text-green-700">{info}</p>
        </div>
      )}

      {/* Current plan */}
      <div className="card mb-6">
        <h2 className="card-header">המסלול הנוכחי</h2>

        {isPro ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 size={24} className="text-green-600" aria-hidden="true" />
              </div>
              <div>
                <p className="text-xl font-semibold text-gray-900">
                  Pro {sub?.planType === 'annual' ? '· שנתי' : sub?.planType === 'monthly' ? '· חודשי' : ''}
                </p>
                <p className="text-sm text-gray-600">גישה מלאה לאפליקציה</p>
              </div>
            </div>

            {sub && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                {sub.cardLast4 && (
                  <div>
                    <p className="text-sm text-gray-600">אמצעי תשלום</p>
                    <p className="font-medium text-gray-900 ltr text-start">
                      {sub.cardBrand || 'כרטיס'} •••• {sub.cardLast4}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600">תשלום אחרון</p>
                  <p className="font-medium text-gray-900">{formatHebrewDate(sub.lastPaymentAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    {sub.planType === 'monthly' && sub.recurring !== false && !sub.cancelAtPeriodEnd
                      ? 'מתחדש ב'
                      : 'תקף עד'}
                  </p>
                  <p className="font-medium text-gray-900">{formatHebrewDate(sub.currentPeriodEnd)}</p>
                </div>
              </div>
            )}

            {isManualMonthly && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                מנוי חודשי בתשלום ידני (ביט / Apple Pay) — אינו מתחדש אוטומטית. כדי להמשיך את הגישה
                יש לחדש את התשלום בכל חודש עד {formatHebrewDate(sub?.currentPeriodEnd)}.
              </div>
            )}

            {sub?.status === 'past_due' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                החיוב האחרון נכשל. כדי שהגישה לא תיפסק יש לעדכן אמצעי תשלום.
              </div>
            )}

            {sub?.cancelAtPeriodEnd && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700">
                המנוי בוטל ולא יתחדש. הגישה תישמר עד {formatHebrewDate(sub.currentPeriodEnd)}.
              </div>
            )}

            {isManualMonthly && (
              <button
                onClick={() => setPendingPay({ planId: 'monthly', method: 'bit' })}
                disabled={working !== null || !paymentAvailable}
                className="btn btn-primary"
              >
                {working === 'monthly-bit' ? 'מעביר לעמוד התשלום...' : 'חידוש המנוי (ביט / Apple Pay)'}
              </button>
            )}

            {sub?.planType === 'monthly' && sub?.recurring !== false && !sub?.cancelAtPeriodEnd && (
              <button
                onClick={handleCancel}
                disabled={working === 'cancel'}
                className="btn btn-outline text-red-600 border-red-200 hover:bg-red-50"
              >
                {working === 'cancel' ? 'מבטל...' : 'ביטול מנוי'}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <CreditCard size={24} className="text-gray-600" aria-hidden="true" />
              </div>
              <div>
                <p className="text-xl font-semibold text-gray-900">Free</p>
                <p className="text-sm text-gray-600">המסלול הבסיסי</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 pt-2 border-t border-gray-100">
              כדי לשוחח עם היועץ יש לבחור אחד מהמסלולים הבאים.
            </p>
          </div>
        )}
      </div>

      {/* Coupon code — only for non-pro users */}
      {!isPro && plans && (
        <div className="card mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Tag size={18} className="text-primary-600" aria-hidden="true" />
            <h2 className="text-lg font-semibold text-gray-900">קוד קופון</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <label htmlFor="coupon-code" className="sr-only">קוד קופון</label>
            <input
              id="coupon-code"
              type="text"
              value={couponInput}
              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              placeholder="הזינו קוד קופון"
              aria-label="קוד קופון"
              disabled={couponChecking}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm ltr text-start focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <button
              onClick={handleApplyCoupon}
              disabled={couponChecking || !couponInput.trim()}
              className="btn btn-primary sm:w-auto"
            >
              {couponChecking ? 'בודק...' : 'החל קופון'}
            </button>
            {appliedCode && (
              <button onClick={handleClearCoupon} className="btn btn-outline sm:w-auto">
                הסר
              </button>
            )}
          </div>
          {couponMessage && (
            <p className={`text-sm mt-2 ${couponMessage.ok ? 'text-green-700' : 'text-red-600'}`}>
              {couponMessage.text}
            </p>
          )}
        </div>
      )}

      {/* Plan cards — only for non-pro users */}
      {!isPro && plans && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Annual */}
          <div className="card border-primary-200 bg-primary-50/30 flex flex-col">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-primary-100 rounded-lg">
                <Sparkles size={24} className="text-primary-600" aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">מסלול שנתי</h2>
                <p className="text-sm text-gray-600">תשלום חד-פעמי · גישה ל-12 חודשים</p>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 mb-4 border border-primary-100">
              {renderPrice('annual', plans.annual.amountIls, '₪ / שנה')}
              <p className="text-xs text-gray-500 mt-1">
                חיוב חד-פעמי · אינו מתחדש · חשבונית מס/קבלה תונפק אוטומטית
              </p>
              <p className="text-xs text-gray-500 mt-1">ניתן לשלם בכרטיס אשראי, בביט או ב-Apple Pay.</p>
            </div>
            <button
              onClick={() => setPendingPay({ planId: 'annual', method: 'card' })}
              disabled={working !== null || !paymentAvailable}
              className="btn btn-primary w-full mt-auto"
            >
              {working === 'annual' ? 'מעביר לעמוד התשלום...' : 'בחירת מסלול שנתי'}
            </button>
          </div>

          {/* Monthly (הוראת קבע) */}
          <div className="card border-gray-200 flex flex-col">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-gray-100 rounded-lg">
                <CalendarClock size={24} className="text-gray-600" aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">מסלול חודשי</h2>
                <p className="text-sm text-gray-600">הוראת קבע באשראי · או תשלום חודשי בביט / Apple Pay</p>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 mb-4 border border-gray-100">
              {renderPrice('monthly', plans.monthly.amountIls, '₪ / חודש')}
              <p className="text-xs text-gray-500 mt-1">
                חיוב חודשי מתחדש עד לביטול · חשבונית מס/קבלה בכל חיוב
              </p>
            </div>

            {MONTHLY_ENABLED ? (
              <>
                <label className="flex items-start gap-2 mb-4 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1"
                  />
                  <span>
                    אני מאשר/ת הוראת קבע לחיוב חודשי של{' '}
                    {couponPreviews.monthly?.valid && couponPreviews.monthly.discountedIls != null
                      ? couponPreviews.monthly.discountedIls
                      : plans.monthly.amountIls}{' '}
                    ₪, המתחדשת אוטומטית עד לביטולה. ניתן לבטל בכל עת מעמוד המנוי.
                  </span>
                </label>
                <button
                  onClick={() => setPendingPay({ planId: 'monthly', method: 'card' })}
                  disabled={working !== null || !paymentAvailable || !consent}
                  className="btn btn-primary w-full"
                >
                  {working === 'monthly' ? 'מעביר לעמוד התשלום...' : 'כרטיס אשראי — חיוב חודשי מתחדש'}
                </button>

                <div className="relative my-3 text-center">
                  <span className="text-xs text-gray-400 bg-white px-2">או</span>
                </div>

                <button
                  onClick={() => setPendingPay({ planId: 'monthly', method: 'bit' })}
                  disabled={working !== null || !paymentAvailable}
                  className="btn btn-outline w-full mt-auto"
                >
                  {working === 'monthly-bit' ? 'מעביר לעמוד התשלום...' : 'תשלום חודשי בביט / Apple Pay'}
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  בביט או ב-Apple Pay: תשלום חד-פעמי לחודש, ללא הוראת קבע — יש לחדש את התשלום בכל חודש.
                </p>
              </>
            ) : (
              <div className="mt-auto">
                <button disabled className="btn btn-primary w-full opacity-60 cursor-not-allowed">
                  בקרוב
                </button>
                <p className="text-xs text-amber-700 mt-2 text-center">
                  מסלול הוראת הקבע ייפתח בקרוב. בינתיים ניתן לבחור במסלול השנתי.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {!isPro && plans && (
        <div className="mt-6 text-center">
          <div className="flex items-center gap-x-4 gap-y-2 justify-center flex-wrap text-sm text-gray-600">
            <span className="font-medium text-gray-700">אמצעי תשלום מקובלים:</span>
            <span className="inline-flex items-center gap-1">
              <CreditCard size={16} aria-hidden="true" /> כרטיס אשראי
            </span>
            <span aria-hidden="true">·</span>
            <span>ביט</span>
            <span aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-1">
              <Apple size={16} aria-hidden="true" /> Apple Pay
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-1">Apple Pay זמין במכשירי Apple ובדפדפן Safari.</p>
        </div>
      )}

      {!isPro && plans && (
        <div className="flex items-start gap-2 mt-6 text-xs text-gray-500 justify-center text-center">
          <ShieldCheck size={16} className="text-green-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p>
            התשלומים מאובטחים ומעובדים על ידי CardCom — חברת סליקה מורשית. פרטי האשראי אינם נשמרים אצלנו.
          </p>
        </div>
      )}

      {!isPro && !paymentAvailable && (
        <p className="text-sm text-amber-700 mt-4 text-center">
          שירות התשלומים אינו פעיל כרגע. אנא נסה שוב מאוחר יותר.
        </p>
      )}

      {/* Onboarding: continue free */}
      {onboarding && !isPro && (
        <div className="text-center mt-6">
          <button
            onClick={handleDeferFree}
            disabled={working !== null}
            className="text-gray-600 hover:text-gray-800 underline text-sm"
          >
            {working === 'defer' ? 'ממשיך...' : 'המשך בינתיים (מסלול חינמי)'}
          </button>
        </div>
      )}

      <PaymentReassuranceModal
        isOpen={pendingPay !== null}
        planLabel={
          pendingPay?.planId === 'annual'
            ? 'מסלול שנתי'
            : pendingPay?.method === 'bit'
            ? 'מסלול חודשי (ביט / Apple Pay)'
            : 'מסלול חודשי (כרטיס אשראי)'
        }
        amountIls={pendingPay ? effectiveAmount(pendingPay.planId) : 0}
        priceSuffix={pendingPay?.planId === 'annual' ? '₪ / שנה' : '₪ / חודש'}
        working={working !== null}
        onConfirm={() => pendingPay && handleUpgrade(pendingPay.planId, pendingPay.method)}
        onClose={() => {
          if (working === null) setPendingPay(null)
        }}
      />
    </div>
  )
}
